import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOW,
  YELLOW,
} from 'styles/tokens';

export interface JsonPayloadEntry {
  label: string;
  data: object | string | null | undefined;
  filename: string;
}

interface JsonViewerDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  payloads: JsonPayloadEntry[];
}

const formatJson = (data: object | string | null | undefined): string => {
  if (!data) return '';
  if (typeof data === 'string') {
    try {
      return JSON.stringify(JSON.parse(data), null, 2);
    } catch {
      return data;
    }
  }
  return JSON.stringify(data, null, 2);
};

const downloadJsonFile = (jsonString: string, filename: string) => {
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const copyToClipboard = (text: string): Promise<void> =>
  navigator.clipboard.writeText(text).catch(() => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  });

const JsonViewerDialog: React.FC<JsonViewerDialogProps> = ({ open, onClose, title, payloads }) => {
  const [copied, setCopied] = useState(false);

  const availablePayloads = payloads.filter((p) => p.data);

  const handleCopy = (jsonString: string) => {
    copyToClipboard(jsonString).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              backdropFilter: GLASS_BLUR,
              backgroundColor: GLASS_BACKGROUND,
              boxShadow: SHADOW,
              borderRadius: RADIUS_CARD,
              border: `1px solid ${alpha(NAVY, 0.06)}`,
              width: '95vw',
              maxWidth: '1400px',
              maxHeight: '85vh',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            color: NAVY,
            pb: 0,
          }}
        >
          {title}
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 2 }}>
          {availablePayloads.length === 0 ? (
            <Typography sx={{ color: alpha(NAVY, 0.5), fontStyle: 'italic', py: 3, textAlign: 'center' }}>
              No hay datos disponibles para este lead
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                mt: 1,
              }}
            >
              {availablePayloads.map((payload) => {
                const jsonString = formatJson(payload.data);
                return (
                  <Box
                    key={payload.label}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Column header with label + actions */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 0.75,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: alpha(NAVY, 0.55),
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          fontSize: '0.65rem',
                        }}
                      >
                        {payload.label}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Copiar" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(jsonString)}
                            sx={{
                              backgroundColor: alpha(NAVY, 0.06),
                              color: NAVY,
                              '&:hover': { backgroundColor: alpha(NAVY, 0.12) },
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Descargar" arrow>
                          <IconButton
                            size="small"
                            onClick={() => downloadJsonFile(jsonString, payload.filename)}
                            sx={{
                              backgroundColor: YELLOW,
                              color: NAVY,
                              '&:hover': { backgroundColor: alpha(YELLOW, 0.85) },
                            }}
                          >
                            <DownloadIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    {/* JSON content */}
                    <Box
                      sx={{
                        backgroundColor: SURFACE_CONTAINER_LOW,
                        borderRadius: RADIUS_ITEM,
                        p: 2,
                        flex: 1,
                        maxHeight: '65vh',
                        overflow: 'auto',
                      }}
                    >
                      <Typography
                        component="pre"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.72rem',
                          lineHeight: 1.5,
                          color: NAVY,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          m: 0,
                        }}
                      >
                        {jsonString}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Copiado al portapapeles"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default JsonViewerDialog;
